import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Wallet, Scale, Clock, TrendingUp, FileSpreadsheet, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import KpiCard from '../../components/KpiCard';
import SectionCard from '../../components/SectionCard';
import { ChartBox, COLORS, EmptyChart } from '../registros/chartParts';
import { fetchAnalisisNomina } from '../../services/reportesV2.services';
import { exportarAnalisisNomina } from './analisisNominaExcel';
import InfoTip from '../../components/InfoTip';
import {
  explicaDevengado,
  explicaNeto,
  explicaCostoEmpresa,
  explicaPorKg,
  explicaPorHora,
  explicaRecargosExtras,
  explicaRepartoPlantas,
  explicaParticipacion,
} from './explicaciones';

const pesos = (v) =>
  v == null ? '—' : Number(v).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const millones = (v) => `$${(Number(v || 0) / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
const num = (v, d = 0) => (v == null ? '—' : Number(v).toLocaleString('es-CO', { maximumFractionDigits: d }));
const pct = (v) => `${(Number(v || 0) * 100).toLocaleString('es-CO', { maximumFractionDigits: 1 })} %`;

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const etiquetaPeriodo = (p) => {
  const [, m, d1] = p.desde.split('-').map(Number);
  const [, m2, d2] = p.hasta.split('-').map(Number);
  return m === m2 ? `${d1}–${d2} ${MESES[m - 1]}` : `${d1} ${MESES[m - 1]}–${d2} ${MESES[m2 - 1]}`;
};

// Paleta para las plantas en la gráfica apilada (las demás van en "Otras").
const PALETA = ['#0F3460', '#0E9F6E', '#E9A13B', '#FC536D', '#7BA7D4', '#8B5CF6', '#14B8A6', '#F97316'];
const TOP_PLANTAS = 7;

const millonesEje = (v) => `${(v / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 0 })} M`;

/**
 * Análisis de las nóminas generadas en el rango (todas las plantas): evolución del costo,
 * comparación con kilos recibidos y participación de cada planta.
 */
const AnalisisNominaContent = ({ startDate, endDate, plantasFiltro = [], laundries = [] }) => {
  const { toast } = useToast();
  const desde = new Date(startDate).toLocaleDateString('en-CA');
  const hasta = new Date(endDate).toLocaleDateString('en-CA');
  const [estado, setEstado] = useState({ loading: true, error: null, data: null });

  const cargar = useCallback(async () => {
    setEstado((e) => ({ ...e, loading: true, error: null }));
    try {
      setEstado({ loading: false, error: null, data: await fetchAnalisisNomina({ desde, hasta, centros: plantasFiltro }) });
    } catch (err) {
      setEstado({ loading: false, error: err.message, data: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta, plantasFiltro.join(',')]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const d = estado.data;

  const serie = useMemo(
    () =>
      (d?.periodos || []).map((p) => ({
        periodo: etiquetaPeriodo(p),
        Básico: Math.round(p.basico),
        'Aux. transporte': Math.round(p.auxilio),
        Recargos: Math.round(p.recargos),
        Extras: Math.round(p.extras),
        Aportes: Math.round(p.aportes),
        Prestaciones: Math.round(p.prestaciones),
        Kilos: Math.round(p.kilos),
        'Costo por kg': p.costoKg ? Math.round(p.costoKg) : null,
        'Costo empresa por kg': p.costoEmpresaKg ? Math.round(p.costoEmpresaKg) : null,
        'Costo por hora': p.costoHora ? Math.round(p.costoHora) : null,
        'Kg por hora': p.kgHora ? Number(p.kgHora.toFixed(1)) : null,
      })),
    [d],
  );

  // Costo por planta en cada nómina: las principales plantas y el resto como "Otras".
  const { seriePlantas, plantas } = useMemo(() => {
    if (!d) return { seriePlantas: [], plantas: [] };
    const top = d.centros.slice(0, TOP_PLANTAS).map((c) => c.id);
    const nombres = Object.fromEntries(d.centros.map((c) => [c.id, c.nombre]));
    const filas = d.periodos.map((p) => {
      const fila = { periodo: etiquetaPeriodo(p) };
      for (const cp of d.centroPeriodo.filter((x) => x.nominaId === p.id)) {
        const clave = top.includes(cp.centroId) ? nombres[cp.centroId] : 'Otras';
        fila[clave] = (fila[clave] || 0) + Math.round(cp.devengado);
      }
      return fila;
    });
    const lista = top.map((id) => nombres[id]);
    if (d.centros.length > TOP_PLANTAS) lista.push('Otras');
    return { seriePlantas: filas, plantas: lista };
  }, [d]);

  const participacion = useMemo(
    () =>
      (d?.centros || []).map((c) => ({
        planta: c.nombre,
        'Costo de nómina': Number((c.participacionCosto * 100).toFixed(1)),
        'Kilos recibidos': Number((c.participacionKilos * 100).toFixed(1)),
      })),
    [d],
  );

  if (estado.loading && !d) {
    return (
      <SectionCard title="Análisis de nómina" loading>
        {null}
      </SectionCard>
    );
  }
  if (estado.error) return <SectionCard title="Análisis de nómina" error={estado.error} onReload={cargar} />;
  if (!d) return null;

  const t = d.totales;
  if (!t.nominas) {
    return (
      <SectionCard title="Análisis de nómina">
        <p className="text-sm text-gray-500 py-6 text-center">
          {plantasFiltro.length
            ? 'Las plantas elegidas no tienen costo de nómina en el rango.'
            : 'No hay nóminas generadas en el rango. Créalas en Personal → Nóminas por periodo.'}
        </p>
      </SectionCard>
    );
  }
  const borradores = d.periodos.filter((p) => p.estado === 'borrador').length;
  const nombresPlantas = plantasFiltro
    .map((id) => laundries.find((l) => String(l._id || l.id) === String(id))?.nombre || id)
    .map((n) => String(n).trim());
  const desactualizadas = d.periodos.filter((p) => p.desactualizada);
  const exportar = () =>
    exportarAnalisisNomina(d).catch((err) =>
      toast({ title: 'No se exportó', description: err.message, variant: 'destructive' }),
    );

  return (
    <div className="space-y-4">
      {plantasFiltro.length > 0 && (
        <div className="rounded-xl border border-[#0F3460]/20 bg-[#E7EEF7] dark:bg-[#1a2a3f] dark:border-blue-900 p-3 text-sm text-[#0F3460] dark:text-blue-100">
          <b>Filtrado por {plantasFiltro.length === 1 ? 'planta' : `${plantasFiltro.length} plantas`}:</b> {nombresPlantas.join(', ')}. Se muestra solo
          la parte del costo, las horas y los kilos de estas plantas (el costo de cada persona se reparte por sus horas en
          cada planta).
        </div>
      )}

      {desactualizadas.length > 0 && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
          <b>{desactualizadas.length} nómina(s) desactualizada(s):</b> hay fichas con un salario distinto al usado en el último cálculo (
          {desactualizadas.map((p) => p.nombre).join(', ')}). Recalcúlalas en Personal → Nóminas por periodo para que el
          análisis refleje los salarios actuales.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <KpiCard
          icon={<Wallet className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Devengado"
          info={explicaDevengado(`Suma de las ${t.nominas} nóminas generadas (no anuladas) que caen en el rango.`)}
          value={millones(t.devengado)}
          sub={{ label: 'Nóminas', value: borradores ? `${t.nominas} (${borradores} en borrador)` : t.nominas }}
          sub2={{ label: 'Promedio por nómina', value: millones(t.devengado / t.nominas) }}
        />
        <KpiCard
          icon={<Wallet className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Neto pagado"
          info={explicaNeto}
          value={millones(t.neto)}
          sub={{ label: "Salud y pensión", value: millones(t.devengado - t.neto) }}
          sub2={{ label: 'Promedio por nómina', value: millones(t.neto / t.nominas) }}
        />
        <KpiCard
          icon={<Building2 className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Costo total empresa"
          info={explicaCostoEmpresa(d.parametrosCostoEmpresa, t)}
          value={millones(t.costoEmpresa)}
          sub={{ label: 'Aportes empleador', value: millones(t.aportes) }}
          sub2={{ label: 'Prestaciones (provisión)', value: millones(t.prestaciones) }}
        />
        <KpiCard
          icon={<Scale className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Costo empresa por kg"
          info={explicaPorKg('Costo total empresa')}
          value={pesos(t.costoEmpresaKg)}
          sub={{ label: 'Devengado por kg', value: pesos(t.costoKg) }}
          sub2={{ label: 'Kilos recibidos', value: `${num(t.kilos / 1000, 1)} t` }}
          extra={
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-[11.5px] text-[#43474F] dark:text-gray-400">Kg por hora trabajada</span>
              <b className="text-[13.5px] font-semibold text-[#0F3460] dark:text-gray-200">{num(t.kgHora, 1)}</b>
            </div>
          }
        />
        <KpiCard
          icon={<Clock className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Devengado por hora trabajada"
          info={explicaPorHora}
          value={pesos(t.costoHora)}
          sub={{ label: 'Horas trabajadas', value: num(t.horas) }}
          sub2={{ label: 'Plantas', value: d.centros.length }}
        />
        <KpiCard
          icon={<TrendingUp className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Recargos + extras"
          info={explicaRecargosExtras}
          value={pct(t.pctRecargosExtras)}
          unit="del devengado"
          sub={{ label: 'Recargos', value: millones(t.recargos) }}
          sub2={{ label: 'Extras', value: millones(t.extras) }}
        />
      </div>

      <ChartBox
        title="Costo total empresa vs. kilos recibidos"
        info={explicaCostoEmpresa(d.parametrosCostoEmpresa)}
        hint="Barras: costo por nómina (devengado: básico, auxilio, recargos y extras; más aportes y prestaciones). Línea: kilos recibidos en el mismo periodo."
        height={320}
      >
        {serie.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={serie} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="periodo" tick={{ fontSize: 11 }} interval={0} angle={serie.length > 10 ? -35 : 0} textAnchor={serie.length > 10 ? 'end' : 'middle'} height={serie.length > 10 ? 55 : 30} />
              <YAxis yAxisId="costo" tick={{ fontSize: 11 }} tickFormatter={millonesEje} width={52} />
              <YAxis yAxisId="kilos" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${num(v / 1000)} t`} width={48} />
              <Tooltip formatter={(v, n) => (n === 'Kilos' ? `${num(v)} kg` : pesos(v))} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="costo" dataKey="Básico" stackId="c" fill={COLORS.navy} />
              <Bar yAxisId="costo" dataKey="Aux. transporte" stackId="c" fill={COLORS.lightBlue} />
              <Bar yAxisId="costo" dataKey="Recargos" stackId="c" fill={COLORS.amber} />
              <Bar yAxisId="costo" dataKey="Extras" stackId="c" fill={COLORS.coral} />
              <Bar yAxisId="costo" dataKey="Aportes" stackId="c" fill="#8B5CF6" />
              <Bar yAxisId="costo" dataKey="Prestaciones" stackId="c" fill="#CBD5E1" />
              <Line yAxisId="kilos" type="monotone" dataKey="Kilos" stroke={COLORS.green} strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart />
        )}
      </ChartBox>

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartBox
          title="Costo total empresa por kg"
          hint="Costo total empresa (devengado + aportes + prestaciones) ÷ kilos recibidos en cada nómina."
          info={explicaPorKg('Costo total empresa')}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="periodo" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} width={52} tickFormatter={(v) => `$${num(v)}`} domain={['auto', 'auto']} />
              <Tooltip formatter={(v) => pesos(v)} />
              <Line type="linear" dataKey="Costo empresa por kg" stroke={COLORS.navy} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Productividad: kg por hora trabajada" hint="Kilos recibidos ÷ horas trabajadas de la nómina." info={explicaPorHora}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="periodo" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} domain={['auto', 'auto']} />
              <Tooltip formatter={(v) => `${num(v, 1)} kg/h`} />
              <Line type="linear" dataKey="Kg por hora" stroke={COLORS.green} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </ChartBox>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartBox title="Costo por planta en cada nómina" hint={`Las ${TOP_PLANTAS} plantas de mayor costo; el resto en "Otras".`} info={explicaRepartoPlantas} height={320}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={seriePlantas} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="periodo" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={millonesEje} width={52} />
              <Tooltip formatter={(v) => pesos(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {plantas.map((p, i) => (
                <Bar key={p} dataKey={p} stackId="p" fill={p === 'Otras' ? '#CBD5E1' : PALETA[i % PALETA.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox
          title="Participación por planta: costo vs. kilos"
          info={explicaParticipacion}
          hint="Si una planta pesa más en costo que en kilos, su nómina es relativamente más cara."
          height={Math.max(320, participacion.length * 26 + 60)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={participacion} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} %`} />
              <YAxis type="category" dataKey="planta" tick={{ fontSize: 10 }} width={130} />
              <Tooltip formatter={(v) => `${v} %`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Costo de nómina" fill={COLORS.navy} />
              <Bar dataKey="Kilos recibidos" fill={COLORS.green} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
      </div>

      <SectionCard
        title="Por planta"
        info={explicaRepartoPlantas}
        hint="El costo de cada persona se reparte entre plantas según las horas que trabajó en cada una."
        onReload={cargar}
        loading={estado.loading}
        action={
          <Button size="sm" variant="outline" onClick={exportar}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <th className="py-2 pr-3">Planta</th>
                <th className="py-2 px-2 text-right">Devengado</th>
                <th className="py-2 px-2 text-right">% devengado</th>
                <th className="py-2 px-2 text-right">Kilos</th>
                <th className="py-2 px-2 text-right">% kilos</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Dev./kg<InfoTip>{explicaPorKg()}</InfoTip></span></th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Costo empresa<InfoTip>{explicaCostoEmpresa(d.parametrosCostoEmpresa)}</InfoTip></span></th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">C. emp./kg<InfoTip>{explicaPorKg('Costo total empresa')}</InfoTip></span></th>
                <th className="py-2 px-2 text-right">Horas</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Dev./hora<InfoTip>{explicaPorHora}</InfoTip></span></th>
                <th className="py-2 px-2 text-right">Kg/hora</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Rec.+ext.<InfoTip>{explicaRecargosExtras}</InfoTip></span></th>
                <th className="py-2 pl-2 text-right">Personas</th>
              </tr>
            </thead>
            <tbody>
              {d.centros.map((c) => {
                const caro = c.costoKg != null && t.costoKg != null && c.costoKg > t.costoKg * 1.15;
                const barato = c.costoKg != null && t.costoKg != null && c.costoKg < t.costoKg * 0.85;
                return (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-medium whitespace-nowrap">{c.nombre}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{pesos(c.devengado)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{pct(c.participacionCosto)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{num(c.kilos)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{pct(c.participacionKilos)}</td>
                    <td
                      className={`py-2 px-2 text-right tabular-nums font-medium ${caro ? 'text-[#B71D3F]' : barato ? 'text-[#0E9F6E]' : ''}`}
                      title={caro ? 'Más de 15 % sobre el promedio' : barato ? 'Más de 15 % bajo el promedio' : undefined}
                    >
                      {c.costoKg != null ? pesos(c.costoKg) : 'sin kilos'}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{pesos(c.costoEmpresa)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{c.costoEmpresaKg != null ? pesos(c.costoEmpresaKg) : '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{num(c.horas)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{pesos(c.costoHora)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{num(c.kgHora, 1)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{pct(c.pctRecargosExtras)}</td>
                    <td className="py-2 pl-2 text-right tabular-nums">{c.personas}</td>
                  </tr>
                );
              })}
              <tr className="font-semibold">
                <td className="py-2 pr-3">Total</td>
                <td className="py-2 px-2 text-right tabular-nums">{pesos(t.devengado)}</td>
                <td className="py-2 px-2 text-right">100 %</td>
                <td className="py-2 px-2 text-right tabular-nums">{num(t.kilos)}</td>
                <td className="py-2 px-2 text-right">100 %</td>
                <td className="py-2 px-2 text-right tabular-nums">{pesos(t.costoKg)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{pesos(t.costoEmpresa)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{pesos(t.costoEmpresaKg)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{num(t.horas)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{pesos(t.costoHora)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{num(t.kgHora, 1)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{pct(t.pctRecargosExtras)}</td>
                <td className="py-2 pl-2" />
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Por nómina" hint="Nóminas generadas (no anuladas) que caen en el rango.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <th className="py-2 pr-3">Nómina</th>
                <th className="py-2 px-2">Periodo</th>
                <th className="py-2 px-2 text-right">Personas</th>
                <th className="py-2 px-2 text-right">Horas</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Devengado<InfoTip>{explicaDevengado()}</InfoTip></span></th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Neto<InfoTip>{explicaNeto}</InfoTip></span></th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Costo empresa<InfoTip>{explicaCostoEmpresa(d.parametrosCostoEmpresa)}</InfoTip></span></th>
                <th className="py-2 px-2 text-right">Recargos</th>
                <th className="py-2 px-2 text-right">Extras</th>
                <th className="py-2 px-2 text-right">Kilos</th>
                <th className="py-2 px-2 text-right"><span className="inline-flex items-center gap-1 justify-end">Dev./kg<InfoTip>{explicaPorKg()}</InfoTip></span></th>
                <th className="py-2 pl-2 text-right">Kg/hora</th>
              </tr>
            </thead>
            <tbody>
              {d.periodos.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {p.nombre}
                    {p.estado !== 'aprobada' && p.estado !== 'pagada' && (
                      <span className="ml-1.5 text-[10px] text-amber-600">{p.estado}</span>
                    )}
                    {p.desactualizada && (
                      <span className="ml-1.5 text-[10px] text-rose-600" title={p.desactualizada.ejemplos.join('\n')}>
                        desactualizada
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">{etiquetaPeriodo(p)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{p.personas}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{num(p.horas)}</td>
                  <td className="py-2 px-2 text-right tabular-nums font-medium">{pesos(p.devengado)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(p.neto)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(p.costoEmpresa)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(p.recargos)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(p.extras)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{num(p.kilos)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{pesos(p.costoKg)}</td>
                  <td className="py-2 pl-2 text-right tabular-nums">{num(p.kgHora, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

export default AnalisisNominaContent;
